import { Card, ScrollArea, Table } from '@radix-ui/themes';
import { CommonDialog } from 'components/common/dialogs';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { ErrorBoundary } from 'components/common/error-boundary';
import { DataTableAverageRow } from 'components/common/training-data-table/average-row';
import { DataTableHeaderRow } from 'components/common/training-data-table/header-row';
import { DataTableSampleRow } from 'components/common/training-data-table/sample-row';
import { DataTableTargetRow } from 'components/common/training-data-table/target-row';
import { DataTableToggler } from 'components/common/training-data-table/toggler';
import {
  AnnotatedVideoPlayer,
  IAnnotationDetails,
  getFireVideoID,
} from 'components/common/video/annotated-player';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { ITrainingDataTableProps } from 'interfaces/i-training-data-table';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { useCallback, useContext, useMemo, useState } from 'react';
import { ShotsService } from 'services/shots.service';
import { useAuthStore } from 'stores/use-auth-store';

// display training shots within training flow and in pitch details after training is complete
export const TrainingDataTable = (props: ITrainingDataTableProps) => {
  const current = useAuthStore((s) => s.current);

  const { machine } = useContext(MachineContext);

  const [expanded, setExpanded] = useState(true);

  const [dialogArchive, setDialogArchive] = useState<{
    key: number;
    shot: IMachineShot;
  }>();

  const [dialogVideo, setDialogVideo] = useState<
    {
      key: number;
    } & IAnnotationDetails
  >();

  const emptyRows = useMemo(() => {
    const output: number[] = [];
    for (let i = 1; i <= props.maxSamples - props.actualShots.length; i++) {
      output.push(i + props.actualShots.length);
    }
    return output;
  }, [props.maxSamples, props.actualShots]);

  const totalCols = useMemo(() => {
    switch (props.priority) {
      case BuildPriority.Breaks: {
        // 1 label + 1 playback + 3 data + (optional) 1 actions
        return props.discardDisabled ? 5 : 6;
      }

      case BuildPriority.Default:
      case BuildPriority.Spins:
      default: {
        // 1 label + 1 playback + 4 data + (optional) 1 actions
        return props.discardDisabled ? 6 : 7;
      }
    }
  }, [props.priority, props.discardDisabled]);

  const onPlayVideo = useCallback(
    async (shot: IMachineShot) => {
      const videoID = getFireVideoID({
        annotated_video_id: shot.annotated_video_id,
        replay_video_id: shot.replay_video_id,
      });

      if (!videoID) {
        setDialogVideo(undefined);
        return;
      }

      setDialogVideo({
        key: Date.now(),
        isMetric: machine.isMetric,
        shotID: shot._id,
        videoID: videoID,
      });
    },
    [machine.isMetric, setDialogVideo]
  );

  const onArchiveShot = useCallback((shot: IMachineShot) => {
    setDialogArchive({
      key: Date.now(),
      shot: shot,
    });
  }, []);

  return (
    <ErrorBoundary identifier="PTDataTable">
      {expanded ? (
        <Card style={{ padding: 0 }}>
          <ScrollArea
            data-identifier="DataTableScrollBox"
            scrollbars="vertical"
          >
            <Table.Root
              style={{
                maxHeight: '300px',
              }}
            >
              <Table.Header>
                <DataTableHeaderRow
                  {...props}
                  expanded={expanded}
                  onToggle={() => setExpanded((prev) => !prev)}
                />
              </Table.Header>
              <Table.Body>
                <DataTableTargetRow {...props} />
                <DataTableAverageRow {...props} />
                {props.actualShots
                  .sort(
                    // ascending order of _created
                    (a, b) => StringHelper.safeSort(a._created, b._created)
                  )
                  .map((s, i) => (
                    <DataTableSampleRow
                      index={i}
                      key={i}
                      shot={s}
                      discardDisabled={props.discardDisabled}
                      isDiscarded={!!props.discardedShotIDs?.includes(s._id)}
                      onArchiveShot={() => {
                        if (props.onDiscard) {
                          props.onDiscard(s);
                          return;
                        }

                        onArchiveShot(s);
                      }}
                      onPlayVideo={onPlayVideo}
                      priority={props.priority}
                      targetBS={props.targetBS}
                      targetTraj={props.targetTraj}
                      targetBreaks={props.targetBreaks}
                    />
                  ))}

                {emptyRows.map((n) => (
                  <Table.Row key={`padding-row-${n}`}>
                    <Table.Cell colSpan={totalCols}>
                      {t('tr.sample')} {n}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </ScrollArea>
        </Card>
      ) : (
        <DataTableToggler
          expanded={expanded}
          onToggle={() => setExpanded((prev) => !prev)}
        />
      )}

      {dialogArchive && (
        <CommonConfirmationDialog
          key={dialogArchive.key}
          identifier="PTDataTableConfirmArchive"
          title="common.delete-training-sample"
          content={t('common.delete-training-sample-msg')}
          action={{
            label: 'common.delete',
            color: RADIX.COLOR.DANGER,
            onClick: async () => {
              const updated = await ShotsService.getInstance().updateShot(
                dialogArchive.shot._id,
                {
                  archived: true,
                  process_notes: `Excluded by ${current.email}`,
                }
              );

              if (updated) {
                props.afterArchive?.(dialogArchive.shot._id);
              }
            },
          }}
        />
      )}

      {dialogVideo && (
        <CommonDialog
          key={dialogVideo.key}
          identifier="PTDataTableShotVideo"
          content={<AnnotatedVideoPlayer details={dialogVideo} />}
          width={RADIX.DIALOG.WIDTH.LG}
          onClose={() => setDialogVideo(undefined)}
        />
      )}
    </ErrorBoundary>
  );
};
